import type { TOrganizationSector, TTenantType } from '@escloud/types';
import { ArrowRight, Building2, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { ChoiceCard, RadioDot } from '@/components/onboarding/choice-card';
import { OnboardingShell, StepTitle } from '@/components/onboarding/onboarding-shell';
import { RegionPicker } from '@/components/onboarding/region-picker';
import { WizardStepper } from '@/components/onboarding/wizard-stepper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRegion, useTenant } from '@/context';
import { usePlan } from '@/hooks/use-plans';
import {
    useAbandonPurchaseIntent,
    useCreateIntentCheckoutSession,
    useCreatePurchaseIntent,
    useCurrentPurchaseIntent,
    useWorkspaceActivated,
} from '@/hooks/use-purchase-intent';
import {
    FREE_SUBSCRIBE_STEPS,
    PRICING_ROUTE,
    SUBSCRIBE_PAYMENT_ROUTE,
    SUBSCRIBE_STEPS,
    stepCounter,
} from '@/lib/onboarding-funnel';
import { readPurchaseIntentHint } from '@/lib/purchase-intent-hint';
import { readSelectedPlan } from '@/lib/selected-plan';

const SECTOR_OPTIONS: Array<{ value: TOrganizationSector; label: string }> = [
    { value: 'hospital', label: 'Hospital' },
    { value: 'private_clinic', label: 'Private clinic' },
    { value: 'medical_device_industry', label: 'Medical device industry' },
    { value: 'research', label: 'Research' },
    { value: 'other', label: 'Other' },
];

/**
 * Step 1 of the purchase funnel (ESCLOUD-332): who holds the licence, and
 * where the data lives. Continue upserts the purchase intent on the CHOSEN
 * region's API and immediately asks it for a Checkout Session: `elements`
 * leads to the payment page, `activated` (a free plan, or mock billing) opens
 * the workspace straight away. No tenant exists until the intent completes.
 *
 * The plan comes from the URL (pricing puts it there and Auth0 returns to it)
 * with the session-stored choice as fallback; without either there is nothing
 * to buy and pricing is the only sensible place.
 */
export function AccountStepPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const planId = searchParams.get('plan') ?? readSelectedPlan();
    const { tenantClaims } = useRegion();
    const { storedTenantId } = useTenant();
    const plan = usePlan(planId ?? undefined);

    const hint = useMemo(() => readPurchaseIntentHint(), []);
    const existing = useCurrentPurchaseIntent(hint?.region ?? null);
    const createIntent = useCreatePurchaseIntent();
    const createSession = useCreateIntentCheckoutSession();
    const abandon = useAbandonPurchaseIntent();
    const workspaceActivated = useWorkspaceActivated();

    const [accountType, setAccountTypeState] = useState<TTenantType | null>(null);
    const [orgName, setOrgNameState] = useState('');
    const [sector, setSectorState] = useState<TOrganizationSector | ''>('');
    const [region, setRegionState] = useState('');
    const [prefilled, setPrefilled] = useState(false);
    const [touched, setTouched] = useState(false);

    // Every user-driven setter marks the form touched, which is what stops the
    // prefill effect below from clobbering a choice the user already made
    // while the open-intent query was still in flight.
    const setAccountType = (value: TTenantType) => {
        setTouched(true);
        setAccountTypeState(value);
    };
    const setOrgName = (value: string) => {
        setTouched(true);
        setOrgNameState(value);
    };
    const setSector = (value: TOrganizationSector) => {
        setTouched(true);
        setSectorState(value);
    };
    const setRegion = (value: string) => {
        setTouched(true);
        setRegionState(value);
    };

    // Edit flow: an open intent prefills the form once, and never over
    // something the user already changed. Region is prefilled too; changing
    // it is what moves the intent to another region.
    useEffect(() => {
        if (prefilled) return;
        if (touched) {
            setPrefilled(true);
            return;
        }
        if (!existing.data) return;
        setAccountTypeState(existing.data.tenantType);
        setOrgNameState(existing.data.organizationName ?? '');
        setSectorState(existing.data.sector ?? '');
        setRegionState(existing.data.region);
        setPrefilled(true);
    }, [existing.data, prefilled, touched]);

    if (!planId) {
        return <Navigate to={PRICING_ROUTE} replace />;
    }

    const isFree = plan.data?.price?.unitAmount === 0;
    const steps = isFree ? FREE_SUBSCRIBE_STEPS : SUBSCRIBE_STEPS;
    const isOrganization = accountType === 'organization';
    const valid =
        accountType !== null &&
        region !== '' &&
        (!isOrganization || (orgName.trim().length >= 2 && sector !== ''));
    const submitting = createIntent.isPending || createSession.isPending;

    const submit = async () => {
        if (!valid || !accountType || submitting) return;
        try {
            // A personal intent carries no organization fields: the API
            // refuses them.
            const intent = await createIntent.mutateAsync({
                region,
                plan_id: planId,
                tenant_type: accountType,
                ...(isOrganization
                    ? { organization_name: orgName.trim(), sector: sector as TOrganizationSector }
                    : {}),
            });
            // The previous region's intent is orphaned by a region switch; tell
            // it, best-effort. The daily expiry catches what this misses. If the
            // hinted intent is already gone (the current-intent query answered
            // 404), the abandon is a harmless no-op on the server, which is why
            // it is not gated on that query's outcome.
            if (hint && hint.region !== region) {
                abandon.mutate({ region: hint.region, intentId: hint.intentId });
            }
            const session = await createSession.mutateAsync({ region, intentId: intent.id });
            if (session.mode === 'activated') {
                await workspaceActivated(session.tenantId, region);
                return;
            }
            // replace: going "back" from payment must land on this form, not
            // re-run it.
            navigate(SUBSCRIBE_PAYMENT_ROUTE, { replace: true });
        } catch (error) {
            toast.error(
                error instanceof Error && error.message
                    ? error.message
                    : 'Your details could not be saved. Please try again.'
            );
        }
    };

    const hasSomewhereToGoBack = tenantClaims.length > 0 || storedTenantId !== null;
    const counter = isFree
        ? `${stepCounter(steps, 'account')} · nothing is charged`
        : `${stepCounter(steps, 'account')} · nothing is charged yet`;

    return (
        <OnboardingShell
            stepper={<WizardStepper steps={steps} currentKey="account" />}
            footer={
                <>
                    <div className="flex items-center gap-4">
                        {hasSomewhereToGoBack && (
                            <Button type="button" variant="outline" onClick={() => navigate('/')}>
                                Cancel
                            </Button>
                        )}
                        <span className="text-[13px] text-muted-foreground">{counter}</span>
                    </div>
                    <Button
                        type="button"
                        size="lg"
                        onClick={() => void submit()}
                        disabled={!valid || submitting}
                        className="gap-2"
                    >
                        {submitting ? (
                            'Saving…'
                        ) : isFree ? (
                            'Create workspace'
                        ) : (
                            <>
                                Continue to payment <ArrowRight className="h-3.5 w-3.5" />
                            </>
                        )}
                    </Button>
                </>
            }
        >
            <StepTitle
                title="Account details"
                sub="Two questions before payment: who holds the licence, and where your data lives."
            />

            <div className="flex flex-col gap-6">
                <section className="rounded-xl border border-border bg-card p-5">
                    <h2 className="m-0 mb-3 text-[15px] font-semibold text-foreground">
                        Who is this subscription for?
                    </h2>
                    <div
                        className="grid gap-3 sm:grid-cols-2"
                        role="radiogroup"
                        aria-label="Who is this subscription for?"
                    >
                        <AccountTypeCard
                            selected={accountType === 'personal'}
                            onSelect={() => setAccountType('personal')}
                            icon={<UserRound className="h-5 w-5" strokeWidth={1.75} />}
                            title="Myself"
                            body="Individual licence, invoiced to you."
                        />
                        <AccountTypeCard
                            selected={isOrganization}
                            onSelect={() => setAccountType('organization')}
                            icon={<Building2 className="h-5 w-5" strokeWidth={1.75} />}
                            title="My organization"
                            body="Invoiced to a hospital or company."
                        />
                    </div>
                    {isOrganization && (
                        <div className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-[1.4fr_1fr]">
                            <div className="space-y-1.5">
                                <Label htmlFor="org-name">Organization name</Label>
                                <Input
                                    id="org-name"
                                    value={orgName}
                                    onChange={(e) => setOrgName(e.target.value)}
                                    placeholder="Centre Hospitalier Universitaire de Lyon"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="org-sector">Sector</Label>
                                <Select
                                    value={sector}
                                    onValueChange={(value) => setSector(value as TOrganizationSector)}
                                >
                                    <SelectTrigger id="org-sector" className="w-full">
                                        <SelectValue placeholder="Select a sector" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SECTOR_OPTIONS.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                </section>

                <section className="rounded-xl border border-border bg-card p-5">
                    <h2 className="m-0 text-[15px] font-semibold text-foreground">Data region</h2>
                    <p className="mt-1.5 mb-4 max-w-[76ch] text-sm leading-relaxed text-muted-foreground">
                        This is where your clinical data will be stored and processed.{' '}
                        <span className="font-semibold text-foreground">
                            It cannot be changed after you subscribe.
                        </span>{' '}
                        Pick the region your institution requires. We never choose it from your location.
                    </p>
                    <RegionPicker region={region} onRegionChange={setRegion} />
                </section>
            </div>
        </OnboardingShell>
    );
}

function AccountTypeCard({
    selected,
    onSelect,
    icon,
    title,
    body,
}: {
    selected: boolean;
    onSelect: () => void;
    icon: ReactNode;
    title: string;
    body: string;
}) {
    return (
        <ChoiceCard selected={selected} onSelect={onSelect} aria-label={title} className="p-4">
            <div className="flex items-start gap-3">
                <RadioDot on={selected} />
                <span className={selected ? 'text-primary' : 'text-muted-foreground'}>{icon}</span>
                <div>
                    <div className="text-[14px] font-semibold text-foreground">{title}</div>
                    <div className="text-[13px] leading-snug text-muted-foreground">{body}</div>
                </div>
            </div>
        </ChoiceCard>
    );
}
